import type { Patrol, Person } from '@local/common-lib';
import {
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  MoreVert as MoreIcon,
  Star as PrimaryIcon,
  Delete as RemoveIcon,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import type { FC, MouseEventHandler } from 'react';
import { useState } from 'react';
import type { Cache } from '../../hooks/use-cache.types.js';
import { usePromise } from '../../hooks/use-promise.js';

interface Props {
  assignment: Patrol.Assignment;
  futurePatrol: boolean;
  handleAccept: (patrollerId: string) => void;
  handleDecline: (patrollerId: string) => void;
  handleRemove: (patrollerId: string) => void;
  handleSetPrimary: (patrollerId: string) => void;
  isAdmin: boolean;
  isPrimaryPatroller?: boolean; // if true, this is the primary patroller for the patrol
  patrollerId: string;
  persons: Cache;
}

export const PatrollerAssignmentRow: FC<Props> = ({
  assignment,
  futurePatrol,
  handleAccept,
  handleDecline,
  handleRemove,
  handleSetPrimary,
  isAdmin,
  patrollerId,
  persons,
  isPrimaryPatroller = false,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const result = usePromise(
    () => persons.one<Person.View>(patrollerId),
    [patrollerId],
  );
  const [clickedButton, setClickedButton] = useState<Element>();

  if (assignment.removed) return <></>;

  if (result[2] === 'rejected') {
    return (
      <TableRow
        key={patrollerId}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>{result[1].message}</TableCell>
      </TableRow>
    );
  }

  const patroller = result[0];
  if (!patroller) {
    return (
      <TableRow
        key={patrollerId}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>
          <CircularProgress size="1rem" />
        </TableCell>
      </TableRow>
    );
  }

  const handleOpen: MouseEventHandler<HTMLButtonElement> = (e): void => {
    setClickedButton(e.currentTarget);
  };

  const handleClose = (): void => {
    setClickedButton(undefined);
  };

  const handleAcceptClick = (): void => {
    setClickedButton(undefined);
    handleAccept(patrollerId);
  };

  const handleDeclineClick = (): void => {
    setClickedButton(undefined);
    handleDecline(patrollerId);
  };

  const handleRemoveClick = (): void => {
    setClickedButton(undefined);
    handleRemove(patrollerId);
  };

  return (
    <TableRow key={patrollerId}>
      <TableCell>
        {patroller.firstName} {patroller.lastName.toUpperCase()}
      </TableCell>
      {!smallScreen && <TableCell>{patroller.phone}</TableCell>}
      {!smallScreen && <TableCell>{patroller.email}</TableCell>}
      <TableCell
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        {isPrimaryPatroller && <PrimaryIcon fontSize="small" sx={{ ml: 1 }} />}
        {assignment.accepted ? (
          !assignment.didNotShow ? (
            <Chip
              color="success"
              label="Accepted"
              size="small"
              sx={{ ml: 1 }}
            />
          ) : (
            <Chip
              color="error"
              label="Did not show"
              size="small"
              sx={{ ml: 1 }}
            />
          )
        ) : undefined}
        {assignment.declined ? (
          <Chip color="error" label="Declined" size="small" sx={{ ml: 1 }} />
        ) : undefined}
        {!assignment.accepted && !assignment.declined ? (
          <Box sx={{ fontStyle: 'italic', ml: 1 }}>
            {assignment.emailed ? 'Pending' : 'Not emailed'}
          </Box>
        ) : undefined}
        {isAdmin && futurePatrol && (
          <>
            <IconButton onClick={handleOpen} sx={{ ml: 1 }}>
              <MoreIcon />
            </IconButton>
            <Menu
              anchorEl={clickedButton}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
              }}
              onClose={handleClose}
              open={!!clickedButton}
              transformOrigin={{
                horizontal: 'right',
                vertical: 'top',
              }}
            >
              {!assignment.accepted ? (
                <MenuItem onClick={handleAcceptClick}>
                  <ListItemIcon>
                    <AcceptIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Accept</ListItemText>
                </MenuItem>
              ) : undefined}
              {!assignment.declined ? (
                <MenuItem onClick={handleDeclineClick}>
                  <ListItemIcon>
                    <DeclineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Decline</ListItemText>
                </MenuItem>
              ) : undefined}
              {isPrimaryPatroller ? (
                <MenuItem disabled>
                  <ListItemIcon>
                    <PrimaryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Primary</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem onClick={() => handleSetPrimary(patrollerId)}>
                  <ListItemIcon>
                    <PrimaryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Set as primary</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleRemoveClick}>
                <ListItemIcon>
                  <RemoveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Remove</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </TableCell>
    </TableRow>
  );
};
